import { Fragment, type ReactElement } from "react";
import type {
  ProblemTimelineParams,
  TimelineMilestone,
} from "@playbookai/widget-schemas";
import { Tooltip, TruncatedText } from "../../molecules/index.js";
import { cn } from "../../lib/utils.js";
import { MilestoneDot } from "./MilestoneDot.js";
import {
  KIND_BORDER,
  KIND_LABEL,
  KIND_TEXT,
  certaintyLabel,
  dateMs,
  isoDate,
  keyMilestones,
  shortDate,
  sortMilestones,
  suspectWindowColumns,
  timelineSummary,
} from "./presentation.js";

/**
 * Minimum width of a milestone column. Below this a label has nowhere to go,
 * so the track scrolls instead of compressing — see the overlap note below.
 */
export const MILESTONE_MIN_WIDTH = "9rem";

/**
 * Chronology as an ordered row of equal-weight columns, not a time-proportional
 * axis.
 *
 * Nothing here is positioned by date: each milestone owns one grid column of at
 * least `MILESTONE_MIN_WIDTH`, so labels, markers and dates can never occupy the
 * same x-range however many milestones there are, however close (or identical)
 * their dates, or however narrow the widget. Past the point where the columns
 * stop fitting, the track scrolls **inside** its own `overflow-x-auto` box; the
 * page and canvas never gain a scrollbar. Chronological order and the
 * certainty/kind styling carry the time information the pixel offsets used to.
 */
export function TimelineTrack({
  data,
  compact,
  onMove,
  readOnly,
  selectedId,
  onSelect,
}: {
  data: ProblemTimelineParams;
  compact: boolean;
  onMove?: ((id: string, date: string) => void) | undefined;
  readOnly?: boolean | undefined;
  selectedId?: string | undefined;
  onSelect?: ((id: string) => void) | undefined;
}): ReactElement {
  const milestones = compact
    ? keyMilestones(data.milestones)
    : sortMilestones(data.milestones);
  const summary = timelineSummary(data);
  const band = summary.days === null ? null : suspectWindowColumns(milestones);

  if (milestones.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-4 text-center text-xs italic text-pb-text-muted">
        No milestones yet — add the last-known-good and first-recognised anchors
        to size the suspect window.
      </div>
    );
  }

  return (
    <div
      data-testid={compact ? "problem-timeline" : "problem-timeline-expanded"}
      // `contain-paint` is load-bearing, not decoration: with `overflow-x-auto`
      // alone the scrolled-away columns still widened the *viewport's* scroll
      // area, so a dense timeline made the whole page scroll sideways at 375 /
      // 768px. Paint containment keeps the overflow inside this box, which is
      // what `responsive:check` measures.
      className="min-w-0 contain-paint overflow-x-auto overflow-y-hidden"
    >
      {/* Kept hand-built (design-system adoption): the track is a grid of
          date columns with the suspect-window band spanning cells — grid-track
          cells, not a table or a list, so no design-system part lays it out. */}
      <div
        className="grid min-w-full"
        style={{
          gridTemplateColumns: `repeat(${milestones.length}, minmax(${MILESTONE_MIN_WIDTH}, 1fr))`,
        }}
      >
        {band ? (
          <>
            <div
              className="sr-only"
              style={{
                gridRow: 1,
                gridColumn: `${band.start + 1} / ${band.end + 2}`,
              }}
            >
              Suspect window ({summary.days} day
              {summary.days === 1 ? "" : "s"})
            </div>
            <div
              data-testid="suspect-window-band"
              aria-hidden="true"
              className="rounded-md bg-pb-amber/15"
              style={{
                gridRow: "2 / 5",
                gridColumn: `${band.start + 1} / ${band.end + 2}`,
              }}
            />
          </>
        ) : null}
        {milestones.map((milestone, index) => (
          <Fragment key={milestone.id}>
            <div
              style={{ gridRow: 2, gridColumn: index + 1 }}
              className="min-w-0 px-2 pb-1.5 pt-1"
            >
              <div
                className={cn(
                  "break-words text-center text-xs font-medium",
                  KIND_TEXT[milestone.kind],
                )}
              >
                <TruncatedText lines={2} className="w-full">
                  {milestone.label}
                </TruncatedText>
              </div>
              <span className="sr-only">
                {KIND_LABEL[milestone.kind]} · {certaintyLabel(milestone)} ·{" "}
                {milestone.date}
              </span>
            </div>
            <div
              style={{ gridRow: 3, gridColumn: index + 1 }}
              className="relative flex h-7 items-center justify-center"
            >
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-1/2 h-px bg-pb-border"
              />
              <MilestoneMarker
                milestone={milestone}
                onMove={onMove}
                readOnly={readOnly}
                selected={selectedId === milestone.id}
                onSelect={onSelect}
              />
            </div>
            <div
              style={{ gridRow: 4, gridColumn: index + 1 }}
              className="truncate px-2 pb-2 pt-1.5 text-center text-xs text-pb-text-muted"
            >
              {milestone.estimated ? "~" : ""}
              {shortDate(milestone.date)}
              {milestone.projected ? " (proj)" : ""}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function MilestoneMarker({
  milestone,
  onMove,
  readOnly,
  selected,
  onSelect,
}: {
  milestone: TimelineMilestone;
  onMove?: ((id: string, date: string) => void) | undefined;
  readOnly?: boolean | undefined;
  selected?: boolean;
  onSelect?: ((id: string) => void) | undefined;
}): ReactElement {
  const at = dateMs(milestone.date);
  const movable = onMove !== undefined && !readOnly && Number.isFinite(at);

  if (!onSelect && (onMove === undefined || readOnly)) {
    return <TrackDot milestone={milestone} />;
  }
  return (
    <Tooltip
      content={
        readOnly
          ? "Select milestone"
          : "Select milestone; use left and right arrow keys to shift its date by one day"
      }
    >
      {/* eslint-disable-next-line ds/no-hand-built -- the milestone marker on the timeline track: a movable chart mark whose arrow keys shift its date, not a design-system Button */}
      <button
        type="button"
        aria-label={`${onSelect ? "Select" : "Move"} ${milestone.label}`}
        aria-pressed={onSelect ? selected : undefined}
        onClick={() => onSelect?.(milestone.id)}
        disabled={!onSelect && !movable}
        onKeyDown={(event) => {
          if (!movable) return;
          const direction =
            event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
          if (direction === 0) return;
          event.preventDefault();
          const nextDate = new Date(at);
          nextDate.setDate(nextDate.getDate() + direction);
          onMove?.(milestone.id, isoDate(nextDate.getTime()));
        }}
        className="relative flex h-7 w-7 items-center justify-center rounded focus-ring"
      >
        <TrackDot milestone={milestone} selected={selected} />
      </button>
    </Tooltip>
  );
}

/* A fact is StatusDot's kind tint (today's colour). An estimated or projected
   date keeps today's status-coloured ring: OK-87 drops only the shadow. The
   selected ring stays a circle around the 14px dot, as today (design owner,
   26 Sep 2026): on the 28px button it drew a rounded square. */
function TrackDot({
  milestone,
  selected = false,
}: {
  readonly milestone: TimelineMilestone;
  readonly selected?: boolean | undefined;
}): ReactElement {
  return (
    <span
      data-selected-ring={selected ? "" : undefined}
      className={cn(
        "inline-flex shrink-0 rounded-full",
        selected && "ring-2 ring-pb-teal ring-offset-2 ring-offset-pb-card",
      )}
    >
      <TrackDotMark milestone={milestone} />
    </span>
  );
}

function TrackDotMark({
  milestone,
}: {
  readonly milestone: TimelineMilestone;
}): ReactElement {
  if (!milestone.estimated && !milestone.projected) {
    return <MilestoneDot milestone={milestone} size="lg" />;
  }
  return (
    // Kept hand-built (design-system adoption): a chart mark on the track whose
    // ring colour (pb-purple for two kinds) no StatusDot tone or tint draws.
    <span
      aria-hidden="true"
      className={cn(
        "relative h-3.5 w-3.5 shrink-0 rounded-full border-2",
        KIND_BORDER[milestone.kind],
        milestone.projected && "border-dashed",
      )}
    />
  );
}
